import { getActionError } from "../../utils/actionFeedback";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MdEdit,
  MdOutlineDelete,
  MdDelete,
  MdLocalOffer,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { IoIosCloseCircleOutline } from "react-icons/io";

import "../../index.css";
import { DarkModeContext } from "../../Context/DarkModeContext.jsx";
import axiosInstance from "../../Context/axiosInstance.js";
import DropdownButton from "../../Components/NewAddContentComponents/DropdownButton.jsx";
import CourseImportWizard from "../../Components/CourseImportWizard";
import { toast } from "react-toastify";
import TreeView from "../../Components/TreeView.jsx";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { getSearchProbe, matchesSearch } from "../../utils/search";
import DiscountInput from "../../Components/DiscountInput";
import PriceDisplay from "../../Components/PriceDisplay";
import ManageVisibilityModal from "../../Components/ManageVisibilityModal";
import ManageDiscountModal from "../../Components/ManageDiscountModal";
import ImagePreviewActions from "../../Components/ImagePreviewActions";
import {
  buildCourseCreatePayload,
  buildCourseUpdatePayload,
  courseUrlFields,
  validateCourseUrls,
} from "../../utils/coursePayload";
import { getDiscountAmount, getDiscountPercentage } from "../../utils/pricing";

const toDiscountAmount = (price, discount) => {
  const normalPrice = Number(price);
  const discountAmount = Number(discount);

  if (
    !Number.isFinite(normalPrice) ||
    !Number.isFinite(discountAmount) ||
    normalPrice <= 0 ||
    discountAmount <= 0 ||
    discountAmount > normalPrice
  ) {
    return null;
  }

  return discountAmount;
};

const getCourseFormErrors = ({
  name,
  gradeLevel,
  price,
  storeId,
  folderId,
}) => {
  const errors = {};
  const trimmedName = name?.trim() || "";

  if (!trimmedName) {
    errors.courseName = "Course name is required.";
  } else if (trimmedName.includes("/")) {
    errors.courseName = 'Course name cannot contain "/".';
  }

  if (!gradeLevel) errors.courseGrade = "Academic level is required.";
  if (price === "" || price == null) {
    errors.coursePrice = "Course price is required.";
  } else if (!Number.isFinite(Number(price)) || Number(price) < 0) {
    errors.coursePrice = "Course price must be a valid non-negative number.";
  }
  if (!storeId) errors.courseStore = "Store is required.";
  if (!folderId) errors.courseFolder = "A destination folder is required.";

  return errors;
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  const reasons = Array.isArray(data?.errors)
    ? data.errors
        .map((item) => item?.reason || item?.message)
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    reasons ||
    data?.detail ||
    data?.message ||
    error?.message ||
    "An unexpected error occurred."
  );
};

export default function CoursesList() {
  const { isDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Retrieve values from URL parameters
  const pageFromUrl = parseInt(params.get("page"), 10) || 1;
  const pageSizeFromUrl = parseInt(params.get("pageSize"), 10) || 20;
  const searchQueryFromUrl = params.get("searchQuery") || "";
  const sortColumnFromUrl = params.get("sortColumn") || "Name";
  const sortDirectionFromUrl = params.get("sortDirection") || "asc";

  const [page, setPage] = useState(pageFromUrl);
  const [pageSize, setPageSize] = useState(pageSizeFromUrl);
  const [searchQuery, setSearchQuery] = useState(searchQueryFromUrl);
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery.trim().replace(/\s+/g, " "),
  );
  const latestCourseRequest = useRef(0);
  const editImageInputRef = useRef(null);
  const addImageInputRef = useRef(null);
  const [sortColumn, setSortColumn] = useState(sortColumnFromUrl);
  const [sortDirection, setSortDirection] = useState(sortDirectionFromUrl);

  const [totalPages, setTotalPages] = useState(1);

  const [courses, setCourses] = useState([]);
  const [queuedCourses, setQueuedCourses] = useState([]);
  const [creationProgress, setCreationProgress] = useState("");
  const [academicLevels, setAcademicLevels] = useState([]);
  const [academicLevelsError, setAcademicLevelsError] = useState("");
  const [academicLevelsRetry, setAcademicLevelsRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setAcademicLevelsError("");
    axiosInstance
      .get("/academic-levels")
      .then(({ data }) => {
        if (active) setAcademicLevels(data);
      })
      .catch(() => {
        if (active) setAcademicLevelsError("Could not load academic levels.");
      });
    return () => {
      active = false;
    };
  }, [academicLevelsRetry]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesFetched, setCoursesFetched] = useState(false);
  const [error, setError] = useState(null);

  //states for course options modal
  const [isModalOpen, setModalOpen] = useState(false);
  const [freeCourseConfirmation, setFreeCourseConfirmation] = useState(null);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const [apiErrors, setApiErrors] = useState(null);

  // Store & Folder state management
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  // const [subfolders, setSubfolders] = useState([]);

  //States for the store and folder id columns
  // new state
  // Maps & caches
  const [folderMap, setFolderMap] = useState({});
  const [storeMap, setStoreMap] = useState({});
  const [foldersByStore, setFoldersByStore] = useState({}); // { storeId: treeArray }
  const [isMapsReady, setIsMapsReady] = useState(false);

  //for edit
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editCourseData, setEditCourseData] = useState(null);
  const [loadingEditCourse, setLoadingEditCourse] = useState(false);
  const [editLoadError, setEditLoadError] = useState("");
  const editLoadRequest = useRef(0);
  const [editFolders, setEditFolders] = useState([]);
  const [loadingEditFolders, setLoadingEditFolders] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState([]);

  // State for deleting mode and selected course
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  //Form Data
  const [loadingaddCourse, setLoadingaddCourse] = useState("");
  const [loadingAddImage, setLoadingAddImage] = useState("");
  const [formData, setFormData] = useState({
    courseImageFile: null,
    courseName: "",
    courseGrade: "",
    coursePrice: "",
    courseDiscountAmount: 0,
    courseImageUrl: "",
    courseStore: 0, // storeId as number
    courseFolder: 0, // folderId as number
    courseSubfolder: 0,
  });

  const [selectedFolderId, setSelectedFolderId] = useState(
    formData.courseSubfolder || formData.courseFolder,
  );

  const [formErrors, setFormErrors] = useState({
    courseName: "",
    courseGrade: "",
    coursePrice: "",
    courseImageUrl: "",
    courseImageFile: null,
    courseStore: "",
    courseFolder: "",
  });
  const [editFormErrors, setEditFormErrors] = useState({
    courseName: "",
    courseGrade: "",
    coursePrice: "",
    courseImageUrl: "",
    courseImageFile: null,
    courseStore: "",
    courseFolder: "",
  });

  const storeCurrencyMap = {
    1: "EGP", // Egypt
    3: "AED", // UAE
    4: "SAR", // Saudi Arabia
  };

  const fetchData = useCallback(async () => {
    const requestId = ++latestCourseRequest.current;
    setLoading(true);
    setError("");

    try {
      const response = await axiosInstance.get(
        `/courses?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
          getSearchProbe(debouncedSearchQuery),
        )}`,
      );

      if (requestId !== latestCourseRequest.current) return;
      const returnedCourses = response.data.response || [];
      setCourses(
        debouncedSearchQuery
          ? returnedCourses.filter((course) =>
              matchesSearch(course, debouncedSearchQuery, [
                "name",
                "gradeLevel",
              ]),
            )
          : returnedCourses,
      );
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
    } catch (error) {
      if (requestId !== latestCourseRequest.current) return;
      if (error.response && error.response.status === 401) {
      } else {
        setError(error.message);
      }
    } finally {
      if (requestId === latestCourseRequest.current) {
        setLoading(false);
        setCoursesFetched(true);
      }
    }
  }, [page, pageSize, debouncedSearchQuery, sortColumn, sortDirection]);

  // Fetch folders
  // helper to flatten a folder tree into [{id, name}, ...]
  const flattenFolders = useCallback((folders = []) => {
    const res = [];
    const traverse = (nodes) => {
      for (const f of nodes) {
        res.push({ id: f.id, name: f.name });
        if (f.subFolders && f.subFolders.length) {
          traverse(f.subFolders); // recursive call to inner function
        }
      }
    };

    traverse(folders);
    return res;
  }, []);

  const initData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1) fetch stores (if not already fetched)
      setLoadingStores(true);
      const resStores = await axiosInstance.get(
        `/stores?page=1&pageSize=100&sortColumn=Name&sortDirection=asc&search=`,
      );
      const storeList = resStores.data.response || [];
      setStores(storeList);

      const sMap = {};
      storeList.forEach((s) => {
        sMap[String(s.id)] = s.name;
      });
      setStoreMap(sMap);
      setLoadingStores(false);

      // 2) fetch folder trees for each store in parallel
      const folderPromises = storeList.map((s) =>
        axiosInstance
          .get(`/folders/tree?storeId=${s.id}`)
          .then((res) => (Array.isArray(res.data) ? res.data : []))
          .catch((err) => {
            console.error(`Failed to fetch folder tree for store ${s.id}`, err);
            return [];
          }),
      );

      const allTrees = await Promise.all(folderPromises);

      // build both a flattened global folderMap and per-store trees
      const flatMap = {};
      const byStore = {};
      allTrees.forEach((tree, idx) => {
        const storeId = storeList[idx]?.id;
        byStore[String(storeId)] = tree; // keep full tree for modal selects
        const flattened = flattenFolders(tree);
        flattened.forEach((f) => {
          flatMap[String(f.id)] = f.name;
        });
      });

      setFolderMap(flatMap);
      setFoldersByStore(byStore);

      // mark maps ready
      setIsMapsReady(true);
    } catch (err) {
      console.error("Initialization failed:", err);
      setError(err?.message || "Failed to initialize data");
    } finally {
      setLoading(false);
    }
  }, [flattenFolders]);

  // Call init once on mount
  useEffect(() => {
    initData();
  }, [initData]);

  // After maps are ready, fetch courses once (so folderMap/storeMap are available)
  useEffect(() => {
    if (!isMapsReady) return;
    // fetchData is already defined as useCallback; call it to populate courses
    fetchData();
  }, [isMapsReady, fetchData]);

  useEffect(() => {
    // Reset to page 1 whenever the search query changes
    setPage(1);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const response = await axiosInstance.get(
          `/stores?page=1&pageSize=100&sortColumn=Name&sortDirection=asc&search=`,
        );
        setStores(response.data.response || []);
      } catch (error) {
        console.error("Failed to fetch stores:", error);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, []);

  useEffect(() => {
    const fetchFoldersByStore = async () => {
      if (!formData.courseStore) {
        setFolders([]);
        return;
      }

      try {
        setLoadingFolders(true);
        const response = await axiosInstance.get(
          `/folders/tree?storeId=${formData.courseStore}`,
        );
        setFolders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch folders for store:", error);
        setFolders([]);
      } finally {
        setLoadingFolders(false);
      }
    };

    fetchFoldersByStore();
  }, [formData.courseStore]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen]);

  //handle changing of table page not main page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      navigate(`?page=${newPage}&pageSize=${pageSize}`);
    }
  };

  const handleAddCourseModalOpen = () => {
    setQueuedCourses([]);
    setCreationProgress("");
    setFormData({
      courseImageFile: null,
      courseName: "",
      courseGrade: "",
      coursePrice: "",
      courseDiscountAmount: 0,
      courseImageUrl: "",
      courseStore: 0,
      courseFolder: 0,
      courseSubfolder: 0,
    });
    setSelectedFolderId(null);
    setModalOpen(true);
    setAnimationClass("slide-in");
  };

  const handleFormChange = async (e) => {
    const { id, value, type, files } = e.target;
    if (id === "courseStore") setSelectedFolderId(null);

    if (type === "file") {
      const file = files[0];
      if (file) {
        try {
          // Upload the image and get the URL
          const imageUrl = await uploadImage(file);
          toast.success("Course image uploaded successfully.");
          // Update formData with the image URL and file for preview
          setFormData((prevData) => ({
            ...prevData,
            courseImageUrl: imageUrl,
            courseImageFile: file, // Store file object for preview
          }));
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            courseImageUrl: "",
          }));
        } catch (error) {
          toast.error(
            getActionError(error, "Could not upload the course image."),
          );
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            courseImageUrl: "Image upload failed", // Fixed key
          }));
        }
      }
    } else {
      setFormData((prevData) => {
        const next = { ...prevData, [id]: value };
        if (id === "courseStore") {
          next.courseFolder = 0;
          next.courseSubfolder = 0;
        }
        // If price is set to 0 or empty, clear discount
        if (id === "coursePrice") {
          const numeric = Number(value) || 0;
          if (numeric <= 0) {
            next.courseDiscountAmount = 0;
            next.discountPercentage = 0;
          }
        }
        return next;
      });
    }
  };

  const uploadImage = async (imageFile) => {
    try {
      setLoadingAddImage(true);
      const formDataForImage = new FormData();
      formDataForImage.append("file", imageFile);

      const response = await axiosInstance.post(
        "/attachments/upload-image",
        formDataForImage,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setLoadingAddImage(false);

      const imageUrl = response.data; // Ensure this is correct

      setApiErrors("");
      return imageUrl;
    } catch (error) {
      setLoadingAddImage(false);
      setApiErrors(getApiErrorMessage(error));
      throw error; // Re-throw error to handle it in the caller
    }
  };

  useEffect(() => {
    const fetchFolderTree = async () => {
      const sid = String(formData.courseStore);
      if (!formData.courseStore) {
        setFolders([]);
        return;
      }

      // reuse pre-fetched tree if available
      if (foldersByStore[sid]) {
        setFolders(foldersByStore[sid]);
        return;
      }

      try {
        setLoadingFolders(true);
        const response = await axiosInstance.get(
          `/folders/tree?storeId=${formData.courseStore}`,
        );
        const tree = Array.isArray(response.data) ? response.data : [];
        setFolders(tree);
        // cache it for future use
        setFoldersByStore((prev) => ({ ...prev, [sid]: tree }));
      } catch (error) {
        console.error("Failed to fetch folder tree:", error);
        setFolders([]);
      } finally {
        setLoadingFolders(false);
      }
    };

    fetchFolderTree();
  }, [formData.courseStore, foldersByStore]);

  useEffect(() => {
    const fetchEditFolderTree = async () => {
      if (!editCourseData?.storeId) {
        setEditFolders([]);
        return;
      }

      try {
        setLoadingEditFolders(true);
        const response = await axiosInstance.get(
          `/folders/tree?storeId=${editCourseData.storeId}`,
        );

        const foldersData = Array.isArray(response.data) ? response.data : [];
        setEditFolders(foldersData);
      } catch (error) {
        console.error("❌ Failed to fetch edit folder tree:", error);
        setEditFolders([]);
      } finally {
        setLoadingEditFolders(false);
      }
    };

    fetchEditFolderTree();
  }, [editCourseData?.storeId]);

  const getDraftErrors = (draft) => {
    const errors = getCourseFormErrors({
      name: draft.courseName,
      gradeLevel: draft.courseGrade,
      price: draft.coursePrice,
      storeId: draft.courseStore,
      folderId: draft.courseSubfolder || draft.courseFolder,
    });
    if (
      !Number.isInteger(Number(draft.courseGrade)) ||
      Number(draft.courseGrade) <= 0
    )
      errors.courseGrade = "Select an academic level.";
    if (
      !Number.isInteger(Number(draft.courseStore)) ||
      Number(draft.courseStore) <= 0
    )
      errors.courseStore = "Select a valid store.";
    if (
      !Number.isInteger(Number(draft.courseSubfolder || draft.courseFolder)) ||
      Number(draft.courseSubfolder || draft.courseFolder) <= 0
    )
      errors.courseFolder = "Select a valid destination folder.";
    const amount = Number(draft.courseDiscountAmount) || 0;
    const percentage = Number(draft.discountPercentage) || 0;
    if (amount < 0 || amount > Number(draft.coursePrice))
      errors.discount = "Discount amount must be between zero and the price.";
    if (percentage < 0 || percentage > 100)
      errors.discount = "Discount percentage must be between zero and 100.";
    if (
      !Number.isFinite(Number(draft.courseDiscountAmount)) ||
      !Number.isFinite(Number(draft.discountPercentage ?? 0))
    )
      errors.discount = "Discount must be a valid number.";
    if (amount > 0 && percentage > 0)
      errors.discount =
        "Use either a discount amount or a discount percentage.";
    if (draft.courseImageUrl) {
      try {
        if (
          !["http:", "https:"].includes(new URL(draft.courseImageUrl).protocol)
        )
          throw new Error();
      } catch {
        errors.courseImageUrl = "Image URL must be a valid http or https URL.";
      }
    }
    return { ...errors, ...validateCourseUrls(draft) };
  };

  const emptyNextDraft = (draft) => ({
    ...draft,
    courseName: "",
    coursePrice: "",
    courseDiscountAmount: 0,
    discountPercentage: 0,
    courseImageFile: null,
    courseImageUrl: "",
    practiceUrl: "",
    reviewMyMistakesUrl: "",
    myPerformanceUrl: "",
  });
  const draftHasDetails = (draft) =>
    Boolean(
      draft.courseName?.trim() ||
      (draft.coursePrice !== "" && draft.coursePrice != null) ||
      draft.courseImageUrl ||
      draft.courseImageFile ||
      Number(draft.courseDiscountAmount) ||
      Number(draft.discountPercentage) ||
      courseUrlFields.some(([key]) => draft[key]?.trim()),
    );

  const queueCourse = () => {
    const errors = getDraftErrors(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    setQueuedCourses((items) => [...items, { ...formData }]);
    setFormData(emptyNextDraft(formData));
    setApiErrors("");
  };

  const handleAdd = async (
    e,
    freeCourseConfirmed = false,
    importedDrafts = null,
  ) => {
    e?.preventDefault();
    if (loadingaddCourse || loadingAddImage) {
      if (importedDrafts)
        throw new Error("Please wait for the current save to finish.");
      return;
    }
    const hasCurrent = draftHasDetails(formData);
    const drafts = importedDrafts || [
      ...queuedCourses,
      ...(hasCurrent || !queuedCourses.length ? [formData] : []),
    ];
    const invalidIndex = drafts.findIndex(
      (draft) => Object.keys(getDraftErrors(draft)).length,
    );
    if (invalidIndex >= 0) {
      if (importedDrafts)
        throw new Error(
          "Check course " +
            (invalidIndex + 1) +
            ": " +
            Object.values(getDraftErrors(drafts[invalidIndex])).join(" "),
        );
      setFormErrors(getDraftErrors(drafts[invalidIndex]));
      setApiErrors(
        "Check course " +
          (invalidIndex + 1) +
          ": " +
          Object.values(getDraftErrors(drafts[invalidIndex])).join(" "),
      );
      return;
    }
    if (
      !freeCourseConfirmed &&
      drafts.some(
        (draft) =>
          Number(draft.coursePrice) > 0 &&
          (Number(draft.courseDiscountAmount) === Number(draft.coursePrice) ||
            Number(draft.discountPercentage) === 100),
      )
    ) {
      setFreeCourseConfirmation("add");
      return;
    }
    setLoadingaddCourse(true);
    setFormErrors({});
    setApiErrors("");
    try {
      const payload = [];
      let defaultImageUrl = "";
      for (let index = 0; index < drafts.length; index += 1) {
        const draft = drafts[index];
        setCreationProgress(
          "Preparing images " + (index + 1) + " of " + drafts.length,
        );
        let imageUrl = draft.courseImageUrl;
        if (!imageUrl && draft.courseImageFile)
          imageUrl = await uploadImage(draft.courseImageFile);
        if (!imageUrl) {
          if (!defaultImageUrl) {
            const response = await fetch("/default-course.jpg");
            if (!response.ok)
              throw new Error("Could not load the default course image.");
            const blob = await response.blob();
            defaultImageUrl = await uploadImage(
              new File([blob], "default-course.jpg", {
                type: blob.type || "image/jpeg",
              }),
            );
          }
          imageUrl = defaultImageUrl;
        }
        payload.push(buildCourseCreatePayload(draft, imageUrl));
      }
      setCreationProgress("Creating courses");
      await axiosInstance.post("/courses", payload);
      toast.success(
        drafts.length +
          (drafts.length === 1 ? " course" : " courses") +
          " created successfully.",
      );
      setQueuedCourses([]);
      setFormData(emptyNextDraft(formData));
      setAnimationClass("slide-out");
      setTimeout(() => {
        setModalOpen(false);
        setAnimationClass("");
        setFormErrors({});
        setApiErrors("");
        setSelectedFolderId(null);
      }, 300);
      await fetchData();
    } catch (error) {
      if (importedDrafts) throw error;
      toast.error(getActionError(error, "Could not create courses."));
      setApiErrors(getApiErrorMessage(error));
    } finally {
      setLoadingaddCourse(false);
      setCreationProgress("");
    }
  };

  const loadEditCourse = async (courseId) => {
    const requestId = ++editLoadRequest.current;
    setEditModalOpen(true);
    setLoadingEditCourse(true);
    setEditCourseData(null);
    setEditLoadError("");
    setEditFormErrors({});
    setApiErrors("");
    try {
      const { data } = await axiosInstance.get(`/courses/${courseId}`);
      if (requestId !== editLoadRequest.current) return;
      setEditCourseData({
        ...courses.find((course) => course.id === courseId),
        ...data,
        discountAmountInput:
          Number(data.discountPercentage) > 0 ? 0 : getDiscountAmount(data),
        discountPercentage: data.discountPercentage || 0,
      });
    } catch (error) {
      if (requestId === editLoadRequest.current) {
        setEditLoadError(
          getActionError(error, "Could not load the course details."),
        );
      }
    } finally {
      if (requestId === editLoadRequest.current) setLoadingEditCourse(false);
    }
  };

  const handleEdit = async (freeCourseConfirmed = false) => {
    if (!editCourseData || loadingaddCourse || loadingEditCourse) return;

    const newErrors = getCourseFormErrors({
      name: editCourseData.name,
      gradeLevel: editCourseData.academicLevelId ?? editCourseData.gradeLevel,
      price: editCourseData.price,
      storeId: editCourseData.storeId,
      folderId: editCourseData.folderSubfolder || editCourseData.folderId,
    });
    Object.assign(newErrors, validateCourseUrls(editCourseData));
    const percentage = Number(editCourseData.discountPercentage) || 0;
    const amount = Number(editCourseData.discountAmountInput) || 0;
    if (percentage < 0 || percentage > 100)
      newErrors.discount = "Discount percentage must be between zero and 100.";
    if (amount < 0 || amount > Number(editCourseData.price))
      newErrors.discount =
        "Discount amount must be between zero and the price.";
    setEditFormErrors(newErrors);
    setApiErrors("");

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please correct the highlighted course fields.");
      return;
    }

    const enteredDiscountAmount =
      percentage > 0
        ? (Number(editCourseData.price) * percentage) / 100
        : amount;
    if (
      !freeCourseConfirmed &&
      Number(editCourseData.price) > 0 &&
      Number(enteredDiscountAmount) === Number(editCourseData.price)
    ) {
      setFreeCourseConfirmation("edit");
      return;
    }

    setLoadingaddCourse(true);

    try {
      // Determine imageUrl for edit: upload new image file if provided; else use existing URL if valid;
      // otherwise upload placeholder so server receives an attachment.
      let imageUrl = null;
      if (editCourseData.image) {
        imageUrl = await uploadImage(editCourseData.image);
      } else if (
        editCourseData.imageUrl &&
        editCourseData.imageUrl !== "null" &&
        editCourseData.imageUrl !== ""
      ) {
        imageUrl = editCourseData.imageUrl;
      } else {
        try {
          const resp = await fetch("/default-course.jpg");
          const blob = await resp.blob();
          const placeholderFile = new File([blob], "default-course.jpg", {
            type: blob.type || "image/jpeg",
          });
          imageUrl = await uploadImage(placeholderFile);
        } catch (err) {
          console.error("Failed to upload placeholder image:", err);
          imageUrl = null;
        }
      }

      const discountAmount = toDiscountAmount(
        editCourseData.price,
        enteredDiscountAmount,
      );

      const { data: latestCourse } = await axiosInstance.get(
        `/courses/${editCourseData.id}`,
      );
      await axiosInstance.put(
        `/courses/${editCourseData.id}`,
        buildCourseUpdatePayload(latestCourse, {
          name: editCourseData.name,
          academicLevelId:
            editCourseData.academicLevelId ?? latestCourse.academicLevelId,
          price: Number(editCourseData.price),
          discountAmount: percentage > 0 ? null : discountAmount,
          discountPercentage: percentage || null,
          ...Object.fromEntries(
            courseUrlFields.map(([key]) => [
              key,
              editCourseData[key]?.trim() || null,
            ]),
          ),
          imageUrl,
          storeId: editCourseData.storeId,
          folderId: editCourseData.folderSubfolder
            ? parseInt(editCourseData.folderSubfolder, 10)
            : parseInt(editCourseData.folderId, 10),
        }),
      );

      toast.success("Course updated successfully!");
      setSelectedCourses([]);
      setEditModalOpen(false);
      await fetchData();
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      setError(errorMessage);
      setApiErrors(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingaddCourse(false);
    }
  };

  const handleCloseEditModal = () => {
    if (loadingaddCourse) return;
    editLoadRequest.current += 1;
    setLoadingEditCourse(false);
    setEditModalOpen(false);
    setSelectedCourses([]);
    setFormErrors({});
    setError();
    setApiErrors("");
    setEditFormErrors({
      courseName: "",
      courseGrade: "",
      coursePrice: "",
      courseImageUrl: "",
    });
    setExpandedFolderIds([]);
    setSelectedFolderId(null);
  };

  const handleCloseAddModal = () => {
    if (loadingaddCourse || loadingAddImage) return;
    setAnimationClass("slide-out");
    setTimeout(() => {
      setModalOpen(false);
      setAnimationClass("");
      setFormErrors({});
      setFormData({
        courseImageFile: null,
        courseName: "",
        courseGrade: "",
        coursePrice: "",
        courseDiscountAmount: 0,
        courseImageUrl: "",
        courseStore: 0,
        courseFolder: 0,
        courseSubfolder: 0,
      });
      setQueuedCourses([]);
      setCreationProgress("");
      setError();
      setApiErrors("");
      setSelectedFolderId(null);
      setEditFormErrors({
        courseName: "",
        courseGrade: "",
        coursePrice: "",
        courseImageUrl: "",
      });
    }, 300);
  };

  //HANDLE DELETE CONTENT
  const handleDelete = async () => {
    if (deleting) return;
    if (selectedCourses.length === 0) {
      alert("No courses selected for deletion.");
      return;
    }

    setDeleting(true);

    try {
      // Prepare DELETE requests
      const deleteRequests = selectedCourses.map((courseId) =>
        axiosInstance.delete(`/courses/${courseId}`),
      );

      const results = await Promise.allSettled(deleteRequests);
      const failedIds = selectedCourses.filter(
        (_, index) => results[index].status === "rejected",
      );
      const deletedCount = selectedCourses.length - failedIds.length;
      setSelectedCourses(failedIds);
      setConfirmDeleteModal(false);
      if (deletedCount)
        toast.success(
          deletedCount === 1
            ? "Course deleted successfully"
            : deletedCount + " courses deleted successfully",
        );
      if (failedIds.length)
        toast.error(
          failedIds.length +
            " courses could not be deleted. They remain selected so you can retry.",
        );
      await fetchData();
    } catch (error) {
      // Error handling
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (warnAboutSelectedCustomCourses()) return;

    const percentage = Number(discountPercentage);
    if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
      toast.error("Enter a discount percentage between 1 and 100.");
      return;
    }

    setBulkActionLoading(true);
    try {
      await axiosInstance.post("/courses/discount", {
        courseIds: selectedCourses,
        discountPercentage: percentage,
      });
      toast.success(
        `Discount applied to ${selectedCourses.length === 1 ? "course" : "courses"} successfully.`,
      );
      setDiscountModalOpen(false);
      setDiscountPercentage("");
      setSelectedCourses([]);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getSelectedCustomCourses = () => {
    const selectedIds = new Set(selectedCourses.map(String));

    return courses.filter(
      (course) =>
        selectedIds.has(String(course.id)) &&
        course.gradeLevel?.trim().toLowerCase() === "custom",
    );
  };

  const warnAboutSelectedCustomCourses = () => {
    const customCourses = getSelectedCustomCourses();
    if (customCourses.length === 0) return false;

    const message =
      customCourses.length === 1
        ? `Cannot manage discounts for custom course: ${customCourses[0].name}`
        : `Cannot manage discounts: ${customCourses.length} custom courses are selected.`;

    toast.error(message, {
      toastId: "custom-course-discount-blocked",
      position: "top-center",
      autoClose: 4000,
    });
    return true;
  };

  const openDiscountModal = () => {
    if (warnAboutSelectedCustomCourses()) return;
    const percentages = courses
      .filter((course) => selectedCourses.includes(course.id))
      .map((course) => {
        const amount = getDiscountAmount(course);
        return amount > 0 && Number(course.price) > 0
          ? Number(((amount / Number(course.price)) * 100).toFixed(2))
          : getDiscountPercentage(course);
      });
    const percentage = percentages[0];
    setDiscountPercentage(
      percentage > 0 && percentages.every((value) => value === percentage)
        ? String(percentage)
        : "",
    );
    setDiscountModalOpen(true);
  };

  const discountedCourseIds = courses
    .filter(
      (course) =>
        selectedCourses.some((id) => String(id) === String(course.id)) &&
        getDiscountAmount(course) > 0,
    )
    .map((course) => course.id);

  const handleRemoveDiscount = async () => {
    if (bulkActionLoading || discountedCourseIds.length === 0) return;
    if (warnAboutSelectedCustomCourses()) return;

    setBulkActionLoading(true);
    try {
      await axiosInstance.post("/courses/discount/remove", {
        courseIds: discountedCourseIds,
      });
      toast.success(
        `Discount removed from ${discountedCourseIds.length === 1 ? "course" : "courses"} successfully.`,
      );
      setDiscountModalOpen(false);
      setDiscountPercentage("");
      setSelectedCourses([]);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleVisibilityChange = async (hide) => {
    setBulkActionLoading(true);
    try {
      await axiosInstance.post(`/courses/${hide ? "hide" : "unhide"}`, {
        courseIds: selectedCourses,
      });
      toast.success(
        `${selectedCourses.length === 1 ? "Course" : "Courses"} ${
          hide ? "hidden from" : "shown to"
        } students successfully.`,
      );
      setVisibilityModalOpen(false);
      setSelectedCourses([]);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleCoursePage = (id, name) => {
    if (id) {
      const queryParams = new URLSearchParams({
        page,
        pageSize,
        searchQuery,
        sortColumn,
        sortDirection,
      }).toString();

      navigate(`/courses/${id}?${queryParams}`, {
        state: { name },
      });
    } else {
      console.error("Course ID is undefined");
    }
  };

  //this will control selected courses to be able to edit or delete
  const handleSelectCourses = (courseId) => {
    setSelectedCourses((prevSelected) =>
      prevSelected.includes(courseId)
        ? prevSelected.filter((id) => id !== courseId)
        : [...prevSelected, courseId],
    );
  };

  // control sorting direction
  const toggleSortDirection = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Finds a folder by id and also returns its parentId if exists
  const findFolderById = (folders, id, parentId = null) => {
    for (let folder of folders) {
      if (folder.id === id) {
        return { ...folder, parentId };
      }
      if (folder.subFolders && folder.subFolders.length > 0) {
        const found = findFolderById(folder.subFolders, id, folder.id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!isEditModalOpen || !editCourseData?.folderId || !editFolders?.length) {
      setExpandedFolderIds([]);
      setSelectedFolderId(null);
      return;
    }

    const findPathToFolder = (nodes, targetId, path = []) => {
      for (const node of nodes) {
        const newPath = [...path, node.id];
        if (node.id === targetId) return newPath;
        if (node.subFolders?.length) {
          const result = findPathToFolder(node.subFolders, targetId, newPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findPathToFolder(editFolders, editCourseData.folderId);

    if (path) {
      setExpandedFolderIds(path); // expand all parent folders
      setSelectedFolderId(path[path.length - 1]); // select the deepest folder
    }
  }, [isEditModalOpen, editCourseData?.folderId, editFolders]);

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#292929] text-black" : "bg-white text-black"
      } p-4 lg:p-8 min-h-screen`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } p-4 lg:p-8 mt-[40px] lg:mt-0 rounded-lg shadow-xl flex-1`}
      >
        <div className="mb-4 flex flex-col lg:flex-row lg:justify-between">
          <div className="flex w-full flex-col pb-2 lg:w-auto lg:flex-row lg:justify-between">
            <input
              type="text"
              placeholder="Search courses..."
              aria-label="Search courses"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${
                isDarkMode ? "bg-[#292929]" : "bg-gray-100"
              } w-full px-4 py-2 border border-gray-300 rounded-lg lg:w-auto`}
            />
          </div>
          <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:gap-2">
            <DropdownButton
              handleAddModal={handleAddCourseModalOpen}
              refreshCourses={fetchData}
            />

            <button
              disabled={selectedCourses.length !== 1 || loadingEditCourse}
              onClick={() => loadEditCourse(selectedCourses[0])}
              className={`flex items-center lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-[#2264E5] text-white rounded-lg transition-opacity ${
                selectedCourses.length !== 1
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-blue-600"
              }`}
            >
              <MdEdit size={25} className="pr-2" />
              Edit Course
            </button>

            <button
              type="button"
              onClick={openDiscountModal}
              disabled={selectedCourses.length === 0}
              className={`flex items-center justify-center rounded-lg bg-amber-500 px-4 py-2 text-white transition-opacity lg:px-6 lg:py-3 ${
                selectedCourses.length === 0
                  ? "cursor-not-allowed opacity-40"
                  : "hover:bg-amber-600"
              }`}
            >
              <MdLocalOffer size={25} className="pr-2" />
              Manage Discount
            </button>

            <button
              type="button"
              onClick={() => setVisibilityModalOpen(true)}
              disabled={selectedCourses.length === 0}
              className={`flex items-center justify-center rounded-lg bg-slate-600 px-4 py-2 text-white transition-opacity lg:px-6 lg:py-3 ${
                selectedCourses.length === 0
                  ? "cursor-not-allowed opacity-40"
                  : "hover:bg-slate-700"
              }`}
            >
              <MdVisibility size={25} className="pr-2" />
              Visibility
            </button>

            <button
              onClick={() => {
                setConfirmDeleteModal(true);
              }}
              disabled={selectedCourses.length === 0}
              className={`flex items-center mb-2 lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-red-500 text-white rounded-lg transition-opacity ${
                selectedCourses.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-red-600"
              }`}
            >
              <MdOutlineDelete size={30} className="pr-2" />
              Delete Course
            </button>
          </div>
        </div>
        <div
          className="relative isolate z-0 h-[643px] w-full max-w-full overflow-auto bg-white dark:bg-[var(--study-surface)] custom-scrollbar min-w-0"
          aria-busy={loading}
        >
          <table className="admin-courses-table min-w-full divide-y divide-gray-200">
            <thead
              className={`${
                isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
              } sticky top-0 z-20 shadow-sm`}
            >
              <tr>
                <th className="relative w-14 min-w-[56px] px-4 py-2 text-center font-medium text-gray-500 uppercase tracking-wider">
                  <label className="absolute inset-0 flex cursor-pointer items-center justify-center hover:bg-blue-500/10">
                    <input
                      className="cursor-pointer"
                      type="checkbox"
                      aria-label="Select all courses"
                      onChange={(e) =>
                        setSelectedCourses(
                          e.target.checked
                            ? courses.map((course) => course.id)
                            : [],
                        )
                      }
                      checked={
                        courses.length > 0 &&
                        selectedCourses.length === courses.length
                      }
                    />
                  </label>
                </th>
                <th className=" px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"></th>
                <th
                  onClick={() => toggleSortDirection("Name")}
                  className="cursor-pointer px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                  {sortColumn === "Name" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => toggleSortDirection("GradeLevel")}
                  className="cursor-pointer px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"
                >
                  Grade Level
                  {sortColumn === "GradeLevel" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th className="cursor-default px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="cursor-default px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider">
                  Folder
                </th>
                <th
                  onClick={() => toggleSortDirection("Price")}
                  className="cursor-pointer items-center px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"
                >
                  Price
                  {sortColumn === "Price" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => toggleSortDirection("DiscountPrice")}
                  className="px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"
                >
                  Discounted Price
                  {sortColumn === "DiscountPrice" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => toggleSortDirection("IsHidden")}
                  className="cursor-pointer px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider"
                >
                  Student view
                  {sortColumn === "IsHidden" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody
              className={`${
                isDarkMode ? "text-white" : "text-black"
              } divide-y divide-gray-200`}
            >
              {courses.length > 0 &&
                courses.map((course) => (
                  <tr
                    className={`cursor-pointer animate-zoomIn transition-colors duration-200 motion-reduce:transition-none ${
                      isDarkMode
                        ? "hover:bg-blue-400/10 focus-within:bg-blue-400/10"
                        : "hover:bg-blue-50 focus-within:bg-blue-50"
                    }`}
                    key={course.id}
                  >
                    <td className="relative w-14 min-w-[56px] px-4 py-2 whitespace-nowrap font-medium">
                      <label className="absolute inset-0 flex cursor-pointer items-center justify-center hover:bg-blue-500/10">
                        <input
                          className="cursor-pointer"
                          type="checkbox"
                          aria-label={`Select ${course.name}`}
                          checked={selectedCourses.includes(course.id)}
                          onChange={() => handleSelectCourses(course.id)}
                        />
                      </label>
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.name)}
                      className="cursor-pointer px-4 py-2 text-base"
                    >
                      <div className="flex justify-center items-center gap-3">
                        <img
                          className="w-[70px] max-w-[100px] h-[50px] object-cover rounded-[5px]"
                          src={course.imageUrl}
                          alt=""
                        />
                      </div>
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.name)}
                      className="cursor-pointer items-center gap-3 px-4 py-2 whitespace-normal break-words max-w-[500px] text-base justify-center"
                    >
                      <h2 className="text-[16px] text-center">{course.name}</h2>
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.name)}
                      className="px-4 py-2 whitespace-nowrap text-base text-center"
                    >
                      {course.gradeLevel}
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.store)}
                      className="px-4 py-2 whitespace-nowrap text-base text-center"
                    >
                      {/* {course.storeId} */}

                      {storeMap[String(course.storeId)] ||
                        (isMapsReady ? "Unknown" : "Loading...")}
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.store)}
                      className="px-4 py-2 whitespace-normal text-base text-center break-words max-w-[500px]"
                    >
                      {course.gradeLevel?.trim().toLowerCase() === "custom"
                        ? "Custom"
                        : folderMap[String(course.folderId)] ||
                          (isMapsReady ? "No Folder Yet" : "Loading...")}
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.name)}
                      className="px-4 py-2 whitespace-nowrap text-base text-[#2083D8] text-center"
                    >
                      <PriceDisplay
                        item={course}
                        currency={storeCurrencyMap[course.storeId]}
                      />
                    </td>
                    <td className="px-4 py-2 text-center font-semibold">
                      {course.discountPrice == null
                        ? "—"
                        : `${Number(course.discountPrice).toFixed(2)} ${storeCurrencyMap[course.storeId]}`}
                    </td>
                    <td
                      onClick={() => handleCoursePage(course.id, course.name)}
                      className="px-4 py-2 text-center"
                    >
                      <span
                        title={
                          course.isHidden
                            ? "Hidden from students"
                            : "Visible to students"
                        }
                        aria-label={
                          course.isHidden
                            ? "Hidden from students"
                            : "Visible to students"
                        }
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
                          course.isHidden
                            ? "bg-gray-600 text-gray-200 opacity-50"
                            : "bg-[#2264E5] text-white"
                        }`}
                      >
                        {course.isHidden ? (
                          <MdVisibilityOff size={20} aria-hidden="true" />
                        ) : (
                          <MdVisibility size={20} aria-hidden="true" />
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              {!loading && coursesFetched && courses.length === 0 && (
                <tr className="h-[500px]">
                  <td
                    colSpan="9"
                    className={`${
                      isDarkMode ? "text-white" : "text-gray-500"
                    } text-lg text-gray-500`}
                  >
                    <div className="flex items-center justify-center h-full">
                      No Courses Available
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div
              role="status"
              aria-label="Loading courses"
              className="absolute inset-0 z-40 flex justify-center items-center bg-inherit"
            >
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          )}
        </div>
        {error && <p className="mt-4 text-lg text-red-600">{error.message}</p>}
        <div className="flex items-center justify-end py-4 text-black">
          <label
            htmlFor="pageSize"
            className={`${
              isDarkMode ? "text-white" : "text-gray-700"
            } mr-2 text-sm`}
          >
            Courses per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {[20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"
            }`}
          >
            Previous
          </button>
          <span className={"text-lg mt-2 sm:mt-0"}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page >= totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Next
          </button>
        </div>
      </div>
      {/*/////////////// EDIT COURSE MODAL//////////////////////// */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm sm:p-6 overflow-y-auto">
          <div
            className={`${animationClass} course-form-modal relative flex h-[min(820px,calc(100dvh-2rem))] sm:h-[min(820px,calc(100dvh-3rem))] max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] w-full max-w-[860px] flex-col overflow-hidden rounded-3xl border shadow-2xl transition-all duration-300 ${
              isDarkMode
                ? "course-form-dark border-gray-700 bg-[#191919] text-white"
                : "border-white/70 bg-white text-gray-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close icon */}
            <IoIosCloseCircleOutline
              onClick={handleCloseEditModal}
              className="absolute right-5 top-5 z-10 cursor-pointer text-3xl text-white/80 transition hover:rotate-90 hover:text-white"
            />

            {/* Edit Form */}
            <div
              className={`shrink-0 px-5 py-5 pr-14 sm:px-7 sm:py-6 text-white ${isDarkMode ? "border-b border-[var(--study-border)] bg-[var(--study-raised)]" : "bg-gradient-to-r from-[#06315B] to-blue-500"}`}
            >
              <h2 className="text-2xl font-bold">Edit Course</h2>
              <p className="mt-1 text-sm text-white/70">
                Update course details, pricing, placement, and links.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8">
              {loadingEditCourse ? (
                <div
                  role="status"
                  className="flex min-h-[320px] items-center justify-center text-sm"
                >
                  <span className="dots">Loading course</span>
                </div>
              ) : editLoadError ? (
                <div role="alert" className="py-12 text-center">
                  <p className="text-sm text-red-500">{editLoadError}</p>
                  <button
                    type="button"
                    onClick={() => loadEditCourse(selectedCourses[0])}
                    className="mt-4 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                editCourseData && (
                  <>
                    <fieldset disabled={loadingaddCourse}>
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleEdit();
                        }}
                        noValidate
                        className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 [&>*]:min-w-0 [&_input]:min-w-0 [&_input]:max-w-full [&_select]:min-w-0 [&_select]:max-w-full [&_select]:truncate max-sm:[&_input:not([type=file])]:py-2.5 max-sm:[&_select]:py-2.5"
                      >
                        {/* Image Upload Input */}
                        <div className="flex items-center justify-center sm:col-span-2">
                          <div className="w-full max-w-[360px] min-w-0">
                            <div className="group relative flex h-[160px] sm:h-[220px] w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-blue-300 bg-gray-50 transition hover:border-blue-500 hover:bg-blue-50">
                              {editCourseData?.imageUrl ? (
                                <>
                                  <img
                                    src={editCourseData.imageUrl}
                                    alt="Course Preview"
                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                  />
                                  <ImagePreviewActions
                                    onEdit={() =>
                                      editImageInputRef.current?.click()
                                    }
                                    onDelete={() =>
                                      setEditCourseData((current) => ({
                                        ...current,
                                        image: null,
                                        imageUrl: "",
                                      }))
                                    }
                                  />
                                </>
                              ) : (
                                <label
                                  htmlFor="editCourseImage"
                                  className="flex h-full w-full cursor-pointer items-center justify-center text-gray-500"
                                >
                                  Upload Image
                                </label>
                              )}
                            </div>
                            {/* Display error message */}
                            {editFormErrors.courseImageUrl && (
                              <p className="text-red-500 text-sm mt-1">
                                {editFormErrors.courseImageUrl}
                              </p>
                            )}
                            {apiErrors && (
                              <p className="text-red-500 text-sm mt-2 text-center">
                                {apiErrors}
                              </p>
                            )}

                            <input
                              type="file"
                              id="editCourseImage"
                              ref={editImageInputRef}
                              name="editCourseImage"
                              accept="image/*"
                              onClick={(e) => {
                                e.currentTarget.value = "";
                              }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setEditCourseData((prevData) => ({
                                    ...prevData,
                                    image: file,
                                    imageUrl: URL.createObjectURL(file),
                                  }));
                                }
                              }}
                              className="hidden"
                            />
                          </div>
                        </div>

                        {/* Course Name */}
                        <div className="relative sm:col-span-2">
                          <input
                            type="text"
                            id="editCourseName"
                            placeholder=" "
                            value={editCourseData?.name || ""}
                            onChange={(e) =>
                              setEditCourseData((prevData) => ({
                                ...prevData,
                                name: e.target.value,
                              }))
                            }
                            className="w-full floating-input px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            required
                          />
                          <label
                            htmlFor="editCourseName"
                            className="floating-label"
                          >
                            Course's Name
                          </label>
                          {editFormErrors.courseName && (
                            <p className="text-red-500 text-sm mt-1">
                              {editFormErrors.courseName}
                            </p>
                          )}
                        </div>

                        {/* Course Price */}
                        <div className="relative">
                          <input
                            type="text"
                            id="editCoursePrice"
                            placeholder=" "
                            value={editCourseData?.price ?? ""}
                            onChange={(e) =>
                              setEditCourseData((prevData) => {
                                const val = e.target.value;
                                const numeric = Number(val) || 0;
                                return {
                                  ...prevData,
                                  price: val,
                                  discountAmount:
                                    numeric <= 0 ? 0 : prevData.discountAmount,
                                  discountAmountInput:
                                    numeric <= 0
                                      ? 0
                                      : prevData.discountAmountInput,
                                  discountPercentage:
                                    numeric <= 0
                                      ? 0
                                      : prevData.discountPercentage,
                                };
                              })
                            }
                            className="w-full floating-input px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            required
                          />
                          <label
                            htmlFor="editCoursePrice"
                            className="floating-label"
                          >
                            Course's Price
                          </label>
                          {editFormErrors.coursePrice && (
                            <p className="text-red-500 text-sm mt-1">
                              {editFormErrors.coursePrice}
                            </p>
                          )}
                        </div>
                        <DiscountInput
                          value={editCourseData?.discountAmountInput ?? 0}
                          price={editCourseData?.price}
                          isDarkMode={isDarkMode}
                          onChange={(discountAmountInput) =>
                            setEditCourseData((current) => ({
                              ...current,
                              discountAmountInput,
                              discountPercentage: 0,
                            }))
                          }
                        />

                        {/* Grade Level */}
                        <div>
                          <select
                            id="editCourseGrade"
                            aria-label="Academic level"
                            value={editCourseData?.academicLevelId ?? ""}
                            onChange={(event) =>
                              setEditCourseData((current) => ({
                                ...current,
                                academicLevelId: Number(event.target.value),
                                gradeLevel: academicLevels.find(
                                  (level) =>
                                    String(level.id) === event.target.value,
                                )?.name,
                              }))
                            }
                            className="w-full px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 outline-none"
                          >
                            <option value="">Select Academic Level</option>
                            {academicLevels.map((level) => (
                              <option key={level.id} value={level.id}>
                                {level.name}
                              </option>
                            ))}
                          </select>
                          {academicLevelsError && (
                            <p role="alert" className="text-sm text-red-500">
                              {academicLevelsError}{" "}
                              <button
                                type="button"
                                className="underline"
                                onClick={() =>
                                  setAcademicLevelsRetry((value) => value + 1)
                                }
                              >
                                Retry
                              </button>
                            </p>
                          )}
                          {editFormErrors.courseGrade && (
                            <p className="mt-1 text-sm text-red-500">
                              {editFormErrors.courseGrade}
                            </p>
                          )}
                        </div>

                        <div className="form-group">
                          <select
                            id="editCourseStore"
                            value={editCourseData?.storeId || ""}
                            onChange={(e) => {
                              setSelectedFolderId(null);
                              setExpandedFolderIds([]);
                              setEditCourseData((prevData) => ({
                                ...prevData,
                                storeId: Number(e.target.value),
                                folderId: null,
                                folderSubfolder: null,
                              }));
                            }}
                            className="w-full px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 outline-none"
                          >
                            <option value="">Select a store</option>
                            {loadingStores ? (
                              <option>Loading stores...</option>
                            ) : (
                              stores.map((store) => (
                                <option key={store.id} value={store.id}>
                                  {store.name}
                                </option>
                              ))
                            )}
                          </select>

                          {editFormErrors.courseStore && (
                            <p className="text-red-500 text-sm mt-1">
                              {editFormErrors.courseStore}
                            </p>
                          )}
                        </div>
                        {/* Folder Select */}
                        <div className="h-[180px] sm:h-60 overflow-y-auto rounded-2xl border border-gray-300 bg-white p-3 text-black sm:col-span-2">
                          {!editCourseData.academicLevelId ||
                          !editCourseData.storeId ? (
                            <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-[#2264E5]">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 7.5h6l2 2H21v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-11Z"
                                  />
                                </svg>
                              </div>
                              <p className="text-sm font-semibold text-gray-700">
                                Select an academic level and store first
                              </p>
                              <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
                                Available folders will appear here after both
                                fields are selected.
                              </p>
                            </div>
                          ) : loadingEditFolders ? (
                            <p className="text-center text-gray-500 animate-pulse">
                              Loading folders...
                            </p>
                          ) : editFolders.length > 0 ? (
                            <TreeView
                              nodes={editFolders}
                              selectedNodeId={selectedFolderId}
                              expandedNodeIds={expandedFolderIds}
                              onToggleNode={setExpandedFolderIds}
                              selectable
                              onSelectNode={(id) => {
                                setSelectedFolderId(id);
                                setEditCourseData((prev) => ({
                                  ...prev,
                                  folderId: id,
                                  folderSubfolder: null,
                                }));
                              }}
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">
                              No folders are available for this store.
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="editDiscountPercentage"
                            className="text-sm font-semibold"
                          >
                            Discount percentage (optional)
                          </label>
                          <input
                            id="editDiscountPercentage"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={editCourseData.discountPercentage || ""}
                            onChange={(event) =>
                              setEditCourseData((current) => ({
                                ...current,
                                discountPercentage: event.target.value,
                                discountAmountInput: 0,
                              }))
                            }
                            className="mt-2 w-full rounded-xl border border-gray-300 bg-white p-3 text-black"
                          />
                          {editFormErrors.discount && (
                            <p className="text-sm text-red-500">
                              {editFormErrors.discount}
                            </p>
                          )}
                        </div>
                        {courseUrlFields.map(([key, label]) => (
                          <div key={key}>
                            <label
                              htmlFor={`edit-${key}`}
                              className="text-sm font-semibold"
                            >
                              {label} (optional)
                            </label>
                            <input
                              id={`edit-${key}`}
                              type="url"
                              inputMode="url"
                              value={editCourseData?.[key] || ""}
                              onChange={(event) =>
                                setEditCourseData((current) => ({
                                  ...current,
                                  [key]: event.target.value,
                                }))
                              }
                              disabled={loadingaddCourse}
                              aria-invalid={Boolean(editFormErrors[key])}
                              aria-describedby={
                                editFormErrors[key]
                                  ? `edit-error-${key}`
                                  : undefined
                              }
                              placeholder="https://"
                              className="mt-2 w-full rounded-xl border border-gray-300 bg-white p-3 text-black"
                            />
                            {editFormErrors[key] && (
                              <p
                                id={`edit-error-${key}`}
                                className="mt-1 text-sm text-red-500"
                              >
                                {editFormErrors[key]}
                              </p>
                            )}
                          </div>
                        ))}
                      </form>
                    </fieldset>

                    <div
                      className={`lg:sticky lg:bottom-0 mt-5 flex justify-end border-t pt-4 ${isDarkMode ? "border-gray-700 bg-[#191919]" : "border-gray-100 bg-white"}`}
                    >
                      <button
                        onClick={() => handleEdit()}
                        disabled={loadingaddCourse || loadingAddImage}
                        className="flex items-center rounded-xl bg-[#2264E5] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-lg disabled:pointer-events-none disabled:opacity-50"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-4 h-4 mr-1.5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.5 3.75a6 6 0 0 0-5.98 6.496A5.25 5.25 0 0 0 6.75 20.25H18a4.5 4.5 0 0 0 2.206-8.423 3.75 3.75 0 0 0-4.133-4.303A6.001 6.001 0 0 0 10.5 3.75Zm2.03 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v4.94a.75.75 0 0 0 1.5 0v-4.94l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {loadingaddCourse ? (
                          <span className="dots">Editing</span>
                        ) : (
                          "Save changes"
                        )}
                      </button>
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/*/////////////// ADD COURSE MODAL//////////////////////// */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm sm:p-6 overflow-y-auto">
          <div
            className={`${animationClass} course-form-modal relative flex h-[min(820px,calc(100dvh-2rem))] sm:h-[min(820px,calc(100dvh-3rem))] max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] w-full max-w-[860px] flex-col overflow-hidden rounded-3xl border shadow-2xl transition-all duration-300 ${
              isDarkMode
                ? "course-form-dark border-gray-700 bg-[#191919] text-white"
                : "border-white/70 bg-white text-gray-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close icon */}
            <IoIosCloseCircleOutline
              onClick={handleCloseAddModal}
              className="absolute right-5 top-5 z-10 cursor-pointer text-3xl text-white/80 transition hover:rotate-90 hover:text-white"
            />

            {/* Add Form */}
            <div
              className={`shrink-0 px-5 py-5 pr-14 sm:px-7 sm:py-6 text-white ${isDarkMode ? "border-b border-[var(--study-border)] bg-[var(--study-raised)]" : "bg-gradient-to-r from-[#06315B] to-blue-500"}`}
            >
              <h2 className="text-2xl font-bold">Create courses</h2>
              <p className="mt-1 text-sm text-white/70">
                Add the course details, pricing, and destination folder.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8">
              <div className="mb-4">
                <CourseImportWizard
                  defaults={formData}
                  disabled={Boolean(loadingaddCourse || loadingAddImage)}
                  progress={creationProgress}
                  onCreate={(drafts) => handleAdd(null, true, drafts)}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Import courses across stores and folders using destination
                  names. Review and fix matches before creating.
                </p>
              </div>
              {queuedCourses.length > 0 && (
                <div className="mb-5 space-y-2" aria-label="Queued courses">
                  <h3 className="font-semibold">
                    Ready to create ({queuedCourses.length})
                  </h3>
                  {queuedCourses.map((draft, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 rounded-xl border border-gray-300/30 p-3"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {draft.courseName}
                      </span>
                      <button
                        type="button"
                        disabled={loadingaddCourse}
                        className="text-blue-500"
                        onClick={() => {
                          setQueuedCourses((items) => [
                            ...items.filter((_, i) => i !== index),
                            ...(draftHasDetails(formData) ? [formData] : []),
                          ]);
                          setFormData(draft);
                          setSelectedFolderId(
                            draft.courseSubfolder || draft.courseFolder,
                          );
                          setFormErrors({});
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={loadingaddCourse}
                        className="text-red-500"
                        aria-label={
                          "Remove " + draft.courseName + " from queue"
                        }
                        onClick={() =>
                          setQueuedCourses((items) =>
                            items.filter((_, i) => i !== index),
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <fieldset disabled={loadingaddCourse}>
                <form
                  onSubmit={handleAdd}
                  noValidate
                  className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 [&>*]:min-w-0 [&_input]:min-w-0 [&_input]:max-w-full [&_select]:min-w-0 [&_select]:max-w-full [&_select]:truncate max-sm:[&_input:not([type=file])]:py-2.5 max-sm:[&_select]:py-2.5"
                >
                  {/* Image Upload Input */}
                  <div className="flex items-center justify-center sm:col-span-2">
                    <div className="w-full max-w-[360px] min-w-0">
                      <div className="group relative flex h-[160px] sm:h-[220px] w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-blue-300 bg-gray-50 transition hover:border-blue-500 hover:bg-blue-50">
                        {formData.courseImageFile || formData.courseImageUrl ? (
                          <>
                            <img
                              src={
                                formData.courseImageFile
                                  ? URL.createObjectURL(
                                      formData.courseImageFile,
                                    )
                                  : formData.courseImageUrl
                              }
                              alt="Preview"
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                            />
                            <ImagePreviewActions
                              onEdit={() => addImageInputRef.current?.click()}
                              onDelete={() =>
                                setFormData((current) => ({
                                  ...current,
                                  courseImageFile: null,
                                  courseImageUrl: "",
                                }))
                              }
                            />
                          </>
                        ) : (
                          <label
                            htmlFor="courseImageUrl"
                            className={`text-gray-500 text-center ${
                              loadingAddImage ? "animate-pulse" : ""
                            } flex h-full w-full cursor-pointer items-center justify-center`}
                          >
                            {loadingAddImage ? "Uploading..." : "Upload Image"}
                          </label>
                        )}
                      </div>

                      {/* Error messages */}
                      {formErrors.courseImageUrl && (
                        <p className="text-red-500 text-sm mt-2 text-center">
                          {formErrors.courseImageUrl}
                        </p>
                      )}
                      {apiErrors && (
                        <p className="text-red-500 text-sm mt-2 text-center">
                          {apiErrors}
                        </p>
                      )}

                      <input
                        type="file"
                        id="courseImageUrl"
                        ref={addImageInputRef}
                        name="courseImageUrl"
                        accept="image/*"
                        onClick={(e) => {
                          e.currentTarget.value = "";
                        }}
                        onChange={handleFormChange}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {/* Course Name Input */}
                  <div className="relative sm:col-span-2">
                    <input
                      type="text"
                      id="courseName"
                      placeholder=" "
                      value={formData.courseName}
                      onChange={handleFormChange}
                      className="w-full floating-input px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      required
                    />
                    <label htmlFor="courseName" className="floating-label">
                      Course's Name
                    </label>
                    {formErrors.courseName && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.courseName}
                      </p>
                    )}
                  </div>

                  {/* Course Price Input */}
                  <div className="relative">
                    <input
                      type="text"
                      id="coursePrice"
                      placeholder=" "
                      value={formData.coursePrice}
                      onChange={handleFormChange}
                      className="w-full floating-input px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      required
                    />
                    <label htmlFor="coursePrice" className="floating-label">
                      Course's Price
                    </label>
                    {formErrors.coursePrice && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.coursePrice}
                      </p>
                    )}
                  </div>
                  <DiscountInput
                    value={formData.courseDiscountAmount}
                    price={formData.coursePrice}
                    isDarkMode={isDarkMode}
                    onChange={(courseDiscountAmount) =>
                      setFormData((current) => ({
                        ...current,
                        courseDiscountAmount,
                        discountPercentage: 0,
                      }))
                    }
                  />

                  {/* Grade Level Input */}
                  <div>
                    <select
                      id="courseGrade"
                      aria-label="Academic level"
                      value={formData.courseGrade}
                      onChange={handleFormChange}
                      className="w-full px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 outline-none"
                    >
                      <option value="">Select Academic Level</option>
                      {academicLevels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                    {academicLevelsError && (
                      <p role="alert" className="text-sm text-red-500">
                        {academicLevelsError}{" "}
                        <button
                          type="button"
                          className="underline"
                          onClick={() =>
                            setAcademicLevelsRetry((value) => value + 1)
                          }
                        >
                          Retry
                        </button>
                      </p>
                    )}
                    {formErrors.courseGrade && (
                      <p className="mt-1 text-sm text-red-500">
                        {formErrors.courseGrade}
                      </p>
                    )}
                  </div>
                  {/* Store Input */}
                  <div className="form-group">
                    <select
                      id="courseStore"
                      value={formData.courseStore}
                      onChange={handleFormChange}
                      className="w-full px-3 py-3 mt-2.5 text-sm bg-white text-black rounded-xl border border-gray-300 focus:border-blue-500 outline-none"
                    >
                      <option value="">Select a store</option>
                      {loadingStores ? (
                        <option>Loading stores...</option>
                      ) : (
                        stores.map((store) => (
                          <option key={store.id} value={store.id}>
                            {store.name}
                          </option>
                        ))
                      )}
                    </select>
                    {formErrors.courseStore && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.courseStore}
                      </p>
                    )}
                  </div>

                  {/* Folder Select */}
                  <div className="h-[180px] sm:h-60 overflow-y-auto rounded-2xl border border-gray-300 bg-white p-3 text-black sm:col-span-2">
                    {!formData.courseGrade || !formData.courseStore ? (
                      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-[#2264E5]">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            className="h-6 w-6"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 7.5h6l2 2H21v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-11Z"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-gray-700">
                          Select an academic level and store first
                        </p>
                        <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
                          Available folders will appear here after both fields
                          are selected.
                        </p>
                      </div>
                    ) : loadingFolders ? (
                      <p className="text-center text-gray-500 animate-pulse">
                        Loading folders...
                      </p>
                    ) : folders.length > 0 ? (
                      <TreeView
                        nodes={folders}
                        selectedNodeId={selectedFolderId}
                        selectable
                        onSelectNode={(id) => {
                          const selected = findFolderById(folders, id);
                          setSelectedFolderId(id);
                          setFormData((prev) => ({
                            ...prev,
                            courseFolder: selected.parentId || id,
                            courseSubfolder: selected.parentId ? id : 0,
                          }));
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">
                        No folders are available for this store.
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="discountPercentage"
                      className="text-sm font-semibold"
                    >
                      Discount percentage (optional)
                    </label>
                    <input
                      id="discountPercentage"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.discountPercentage || ""}
                      onChange={(e) =>
                        setFormData((draft) => ({
                          ...draft,
                          discountPercentage: e.target.value,
                          courseDiscountAmount: 0,
                        }))
                      }
                      className="mt-2 w-full rounded-xl border border-gray-300 bg-white p-3 text-black"
                    />
                    {formErrors.discount && (
                      <p className="text-sm text-red-500">
                        {formErrors.discount}
                      </p>
                    )}
                  </div>
                  {courseUrlFields.map(([key, label]) => (
                    <div key={key}>
                      <label htmlFor={key} className="text-sm font-semibold">
                        {label} (optional)
                      </label>
                      <input
                        id={key}
                        type="url"
                        value={formData[key] || ""}
                        onChange={handleFormChange}
                        placeholder="https://"
                        className="mt-2 w-full rounded-xl border border-gray-300 bg-white p-3 text-black"
                      />
                      {formErrors[key] && (
                        <p className="text-sm text-red-500">
                          {formErrors[key]}
                        </p>
                      )}
                    </div>
                  ))}
                </form>
              </fieldset>

              {/* Submit Button */}
              <div
                className={`lg:sticky lg:bottom-0 mt-5 flex justify-end border-t pt-4 ${isDarkMode ? "border-gray-700 bg-[#191919]" : "border-gray-100 bg-white"}`}
              >
                <button
                  type="button"
                  onClick={queueCourse}
                  disabled={loadingaddCourse || loadingAddImage}
                  className="mr-3 rounded-xl border border-blue-500 px-4 py-3 text-sm font-semibold text-blue-500 disabled:opacity-50"
                >
                  Add another course
                </button>
                <button
                  disabled={loadingaddCourse || loadingAddImage}
                  onClick={handleAdd}
                  className="flex items-center rounded-xl bg-[#2264E5] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-lg disabled:pointer-events-none disabled:opacity-50"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-4 h-4 mr-1.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.5 3.75a6 6 0 0 0-5.98 6.496A5.25 5.25 0 0 0 6.75 20.25H18a4.5 4.5 0 0 0 2.206-8.423 3.75 3.75 0 0 0-4.133-4.303A6.001 6.001 0 0 0 10.5 3.75Zm2.03 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v4.94a.75.75 0 0 0 1.5 0v-4.94l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {loadingaddCourse ? (
                    <span>{creationProgress || "Saving"}</span>
                  ) : queuedCourses.length ? (
                    "Create all courses"
                  ) : (
                    "Create course"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ManageDiscountModal
        open={discountModalOpen}
        count={selectedCourses.length}
        percentage={discountPercentage}
        onPercentageChange={setDiscountPercentage}
        onApply={handleApplyDiscount}
        onRemove={handleRemoveDiscount}
        removableCount={discountedCourseIds.length}
        onClose={() => {
          setDiscountModalOpen(false);
          setDiscountPercentage("");
        }}
        loading={bulkActionLoading}
      />

      <ManageVisibilityModal
        open={visibilityModalOpen}
        count={selectedCourses.length}
        itemLabel={selectedCourses.length === 1 ? "course" : "courses"}
        onClose={() => setVisibilityModalOpen(false)}
        onChange={handleVisibilityChange}
        loading={bulkActionLoading}
      />

      {/* FREE COURSE CONFIRMATION MODAL */}
      {freeCourseConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm overflow-y-auto">
          <div
            className={`w-full max-w-md rounded-2xl p-7 shadow-2xl  max-h-[calc(100dvh-2rem)] overflow-y-auto ${
              isDarkMode ? "bg-[#191919] text-white" : "bg-white text-gray-900"
            }  `}
            role="dialog"
            aria-modal="true"
            aria-labelledby="free-course-confirmation-title"
          >
            <h2
              id="free-course-confirmation-title"
              className="text-center text-xl font-bold"
            >
              Make this course free?
            </h2>
            <p
              className={`mt-3 text-center text-sm leading-6 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              The discount equals the full course price. If you continue,
              students will be able to access this course for free.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setFreeCourseConfirmation(null)}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                  isDarkMode
                    ? "bg-gray-700 text-white hover:bg-gray-600"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = freeCourseConfirmation;
                  setFreeCourseConfirmation(null);
                  if (action === "add") {
                    handleAdd(null, true);
                  } else {
                    handleEdit(true);
                  }
                }}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Continue and save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {confirmDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 p-4 overflow-y-auto">
          <div
            className={` max-h-[calc(100dvh-2rem)] overflow-y-auto ${animationClass} w-full max-w-md h-auto    ${
              isDarkMode ? "bg-white text-black" : "bg-white text-black"
            } p-4 sm:p-6 lg:p-8 rounded-lg shadow-lg  `}
          >
            <div className="flex justify-center pb-4">
              <MdDelete className="text-customRed justify-center" size={50} />
            </div>
            <p className="text-lg font-semibold mb-4 text-center">
              Are you sure you want to delete{" "}
              {selectedCourses > 1 ? "this course?" : "these courses?"}
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleDelete(selectedCourses)}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {deleting ? <span className="dots">Deleting</span> : "Yes"}
              </button>
              <button
                onClick={() => setConfirmDeleteModal(false)}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
